﻿import AsyncStorage from '@react-native-async-storage/async-storage';
import * as StoreReview from 'expo-store-review';
import { loadCoachPlan } from '@/lib/coachStorage';
import type { CoachWeekPlan } from '@/lib/coachEngine';
import ProfileGate from '@/components/ProfileGate';
import { createLogger } from '@/lib/logger';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Animated,
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  ActivityIndicator,
  Alert,
  Platform,
  Modal,
  KeyboardAvoidingView,
  AppState,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { supabase } from '@/lib/supabase';
import { V1_LIFT_LABELS, V1_LIFT_POOL, normalizeToV1Lift } from '@/lib/lifts/canonical';
import { fetchBaselineStatus, clearBaselineCache } from '@/lib/baselineClient';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useCameraPermissions } from 'expo-camera';
import { draftStore } from '@/lib/draftStore';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD, BUTTON, SECTION_LABEL } from '@/constants/designTokens';
import { Plus, Calendar, Timer, Calculator, Link, Dumbbell } from 'lucide-react-native';
import { useRouter, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import WorkoutLogs from '@/components/WorkoutLogs';
import TutorialOverlay from '@/components/TutorialOverlay';
import Analytics from '@/components/Analytics';
import MobileExercisePicker from '@/components/MobileExercisePicker';
import EmptyExerciseWarning from '@/components/EmptyExerciseWarning';
import SkeletonRow from '@/components/SkeletonRow';
import AnimatedButton from '@/components/AnimatedButton';
import RestTimer from '@/components/RestTimer';
import WorkoutTimer from '@/components/WorkoutTimer';
import { fetchLastWorkoutEntries, fetchLastWorkoutDate } from '@/lib/repeatWorkout';
import PlateCalculator from '@/components/PlateCalculator';
import { hapticsUtil } from '@/lib/haptics';
import type { MilestoneDef } from '@/lib/milestones';
import MilestoneBanner from '@/components/MilestoneBanner';
import BadgeCelebration from '@/components/BadgeCelebration';
import { BADGE_MAP } from '@/lib/badges';
import Confetti from '@/components/Confetti';
import { EXERCISE_DATABASE } from '@/constants/exerciseDatabase';
import { validateExerciseEntry, isDuplicateSubmission, recordSubmissions, validateSessionSize, validateWorkoutDate } from '@/lib/workoutValidation';
import { Exercise } from '@/types/database';
import {
  ExerciseEntryCard,
  PostOptionModal,
  VideoOptionsModal,
  CameraModal,
  RecordNavHeader,
  DevStrengthPanel,
} from '@/components/record';
import type { ExerciseEntry, RecordView } from '@/components/record';
import { uploadLiftVideo, attachVideoToEvent, checkVideoLimit } from '@/lib/videoProof';
import {
  loadTemplates,
  saveTemplate,
  DAY_SHORT,
  type TemplateStore,
  type TemplateExercise,
} from '@/lib/workoutTemplates';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { useWorkoutForm } from '@/hooks/useWorkoutForm';
import { BUILT_IN_TEMPLATES, getTemplatesByCategory, type BuiltInTemplate } from '@/lib/builtInTemplates';
import { useUserStageLight } from '@/hooks/useUserStage';
import { saveWorkout } from '@/lib/workoutSave';
import { captureError } from '@/lib/errorReporting';
import VisibilityOptInModal from '@/components/VisibilityOptInModal';
import TodaysProgram from '@/components/TodaysProgram';
import WorkoutShareModal from '@/components/share/WorkoutShareModal';
import ConfirmDialog from '@/components/ConfirmDialog';
import type { WorkoutExercise } from '@/components/share/WorkoutShareCard';

const log = createLogger('RECORD');

function showAlert(title: string, message: string) {
  if (Platform.OS === 'web') {
    alert(`${title}: ${message}`);
  } else {
    Alert.alert(title, message);
  }
}

export default function RecordTab() {
  const { profile, session, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();
  const { tier } = useSubscription();
  const form = useWorkoutForm();
  const {
    exerciseEntries, setExerciseEntries,
    workoutName, setWorkoutName,
    sessionNotes, setSessionNotes,
    supersetGroups, setSupersetGroups,
    workoutTimerRunning, setWorkoutTimerRunning, workoutStartTime,
    workoutDate, setWorkoutDate,
    workoutDraftId, setWorkoutDraftId,
    addExerciseEntry, removeExerciseEntry, updateExerciseEntry,
    addSuperset, toggleSupersetMode, supersetMode, addToSuperset, removeFromSuperset,
    reorderExercise,
    resetForm,
  } = form;

  const [milestonesToShow, setMilestonesToShow] = useState<MilestoneDef[]>([]);
  const [badgesToShow, setBadgesToShow] = useState<any[]>([]);
  const [showSaveConfetti, setShowSaveConfetti] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [activeView, setActiveView] = useState<RecordView>('log');
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [selectedBodyPart, setSelectedBodyPart] = useState<string | null>(null);
  const [showExercisePicker, setShowExercisePicker] = useState(false);
  const [currentEntryId, setCurrentEntryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingExercises, setLoadingExercises] = useState(true);
  const [, setExerciseSearch] = useState('');
  const [showPostOption, setShowPostOption] = useState(false);
  const [savedSessionId, setSavedSessionId] = useState<string | null>(null);
  const [showVideoOptions, setShowVideoOptions] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareExercises, setShareExercises] = useState<WorkoutExercise[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraRef, setCameraRef] = useState<any>(null);
  useCameraPermissions();
  const [showEmptyExerciseWarning, setShowEmptyExerciseWarning] = useState(false);
  const [showRestTimer, setShowRestTimer] = useState(false);
  const [restTimerDefault, setRestTimerDefault] = useState(90);
  const [coachPlan, setCoachPlan] = useState<CoachWeekPlan | null>(null);
  const [showPlateCalc, setShowPlateCalc] = useState(false);
  const [plateCalcWeight, setPlateCalcWeight] = useState<number | undefined>(undefined);
  const [nudgeTone, setNudgeTone] = useState<'motivational' | 'fun_trash_talk' | 'serious_direct'>('motivational');
  const [workoutNudgesEnabled, setWorkoutNudgesEnabled] = useState(true);
  const [bodyPartGridWidth, setBodyPartGridWidth] = useState(0);
  const [templateStore, setTemplateStore] = useState<TemplateStore>({});
  const [lastTemplateLoadId, setLastTemplateLoadId] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [activeTemplateKey, setActiveTemplateKey] = useState<string | number | null>(null);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [loadingRepeat, setLoadingRepeat] = useState(false);
  const [lastWorkoutDate, setLastWorkoutDate] = useState<Date | null>(null);
  const [restTimerRef, setRestTimerRef] = useState<{ show: () => void } | null>(null);
  const [duelCount, setDuelCount] = useState(0);
  const [baselineComplete, setBaselineComplete] = useState<boolean | null>(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start();
  }, []);
  const [baselinePromptDismissed, setBaselinePromptDismissedState] = useState(false);
  const setBaselinePromptDismissed = (val: boolean) => {
    setBaselinePromptDismissedState(val);
    // Session-only dismiss - prompt returns next app open until baseline is complete
    // Do NOT persist to AsyncStorage; baseline completion itself gates the banner
  };

  // DEV-only strength_events debug state
  const [devSeCount, setDevSeCount] = useState<number | null>(null);
  const [devSeRows, setDevSeRows] = useState<any[] | null>(null);
  const [devSeLoading, setDevSeLoading] = useState(false);

  const loadDevStrengthEvents = useCallback(async () => {
    if (!__DEV__ || !profile?.id) return;
    setDevSeLoading(true);
    try {
      const { count, error: countError } = await supabase
        .from('strength_events')
        .select('id', { count: 'exact', head: true })
        .eq('profile_id', profile.id);
      if (countError) { setDevSeCount(-1); setDevSeRows([]); return; }
      setDevSeCount(count ?? -1);
      const { data, error: rowsError } = await supabase
        .from('strength_events')
        .select('id,lift_key,performed_at,weight,reps')
        .eq('profile_id', profile.id)
        .order('performed_at', { ascending: false })
        .limit(5);
      setDevSeRows(rowsError ? [] : (data ?? []));
    } catch {
      setDevSeCount(-1); setDevSeRows([]);
    } finally {
      setDevSeLoading(false);
    }
  }, [profile?.id]);

  const [devPingResult, setDevPingResult] = useState<string | null>(null);
  const [devPingLoading, setDevPingLoading] = useState(false);

  const pingStrengthEventsFunction = useCallback(async () => {
    if (!__DEV__) return;
    setDevPingLoading(true); setDevPingResult(null);
    try {
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'insert_strength_events',
        { body: { performed_at: new Date().toISOString(), payload: { exercises: [] }, client_context: { client_platform: Platform.OS, client_app_version: 'dev-ping', timezone_offset_minutes: -(new Date().getTimezoneOffset()) } } },
      );
      setDevPingResult(fnError ? `ERROR: ${fnError.message || 'unknown'}` : `OK: ${JSON.stringify(fnData)}`);
    } catch (e: any) {
      setDevPingResult(`EXCEPTION: ${e.message || e}`);
    } finally {
      setDevPingLoading(false);
    }
  }, []);

  const params = useLocalSearchParams();
  const viewParam = Array.isArray(params.view) ? params.view[0] : params.view;
  const openPickerParam = Array.isArray(params.openPicker) ? params.openPicker[0] : params.openPicker;
  const programIdParam = Array.isArray(params.programId) ? params.programId[0] : params.programId;
  const programDayIndexParam = Array.isArray(params.programDayIndex) ? params.programDayIndex[0] : params.programDayIndex;
  // DUEL Coach: pre-load exercises from JSON param (additive — no core logic changes)
  const coachDayJsonParam = Array.isArray(params.coachDayJson) ? params.coachDayJson[0] : params.coachDayJson;
  const isRecordView = viewParam === 'log' || viewParam === 'logs' || viewParam === 'analytics' || viewParam === 'program';
  const [openedFromBaseline, setOpenedFromBaseline] = useState(false);
  const [programLoaded, setProgramLoaded] = useState('');

  const { stage } = useUserStageLight(profile, {
    hasWorkouts: lastWorkoutDate != null,
    duelCount,
  });

  useEffect(() => {
    loadExercises();
    loadEngagementPreferences();
    if (profile?.id) {
      loadTemplates(profile.id).then(setTemplateStore);
      fetchBaselineStatus(profile.id).then((s) => setBaselineComplete(s?.isComplete ?? false)).catch(() => setBaselineComplete(false));
      fetchLastWorkoutDate(profile.id).then(setLastWorkoutDate).catch(() => {});
      // Fetch duel count for stage detection
      (async () => {
        try {
          const { count } = await supabase
            .from('duels')
            .select('id', { count: 'exact', head: true })
            .or(`challenger_id.eq.${profile.id},challenged_id.eq.${profile.id}`);
          setDuelCount(count ?? 0);
        } catch { /* ignore */ }
      })();
    }
  }, [profile?.id]);

  useFocusEffect(useCallback(() => {
    setActiveView(isRecordView ? (viewParam as RecordView) : 'log');
    if (openPickerParam === '1') { setOpenedFromBaseline(true); setShowExercisePicker(true); }
    // Load coach plan for Program tab
    if (profile?.id) loadCoachPlan(profile.id).then(p => p && setCoachPlan(p)).catch(() => {});
  }, [isRecordView, viewParam, openPickerParam, profile?.id]));

  // Load program exercises when navigated with programId param
  useFocusEffect(useCallback(() => {
    if (!programIdParam || !programDayIndexParam) return;
    const loadKey = `${programIdParam}:${programDayIndexParam}`;
    if (loadKey === programLoaded) return;
    setProgramLoaded(loadKey);
    (async () => {
      try {
        const { data: prog } = await supabase
          .from('coach_programs')
          .select('days')
          .eq('id', programIdParam)
          .single();
        if (!prog?.days) return;
        const days = prog.days as { exercises: { exerciseName: string; targetSets: number; targetReps: number; targetWeight?: number; rpe?: number; notes?: string }[] }[];
        const dayIdx = parseInt(programDayIndexParam, 10);
        const day = days[dayIdx];
        if (!day?.exercises?.length) return;
        const entries: ExerciseEntry[] = day.exercises.map((pex) => {
          const matched = exercises.find((e) => e.name.toLowerCase() === pex.exerciseName.toLowerCase()) ?? null;
          return {
            id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
            exercise: matched ?? ({ id: `prog-${pex.exerciseName}`, name: pex.exerciseName, body_part: '', is_custom: false } as any),
            weight: pex.targetWeight ? String(pex.targetWeight) : '',
            reps: String(pex.targetReps || ''),
            sets: String(pex.targetSets || 1),
            isPR: false,
            videoUri: null,
            notes: pex.notes || '',
          };
        });
        setExerciseEntries(entries);
        setActiveView('log');
      } catch (err) {
        console.warn('[Record] Failed to load program exercises:', err);
      }
    })();
  }, [programIdParam, programDayIndexParam, exercises, programLoaded]));

  // DUEL Coach: load pre-built day from coachDayJson param (additive, no core logic changes)
  const [coachDayLoaded, setCoachDayLoaded] = useState('');
  useFocusEffect(useCallback(() => {
    if (!coachDayJsonParam) return;
    if (coachDayJsonParam === coachDayLoaded) return;
    setCoachDayLoaded(coachDayJsonParam);
    try {
      const coachExercises = JSON.parse(coachDayJsonParam) as Array<{ name: string; sets: number; reps: string; restSeconds?: number; rpe?: number; notes?: string }>;
      if (!Array.isArray(coachExercises) || coachExercises.length === 0) return;
      const entries: ExerciseEntry[] = coachExercises.map((ce) => {
        const matched = exercises.find((e) => e.name.toLowerCase() === ce.name.toLowerCase()) ?? null;
        const repMin = ce.reps.includes('-') ? ce.reps.split('-')[0] : ce.reps;
        // Pre-populate all sets so the record screen shows the correct number of rows
        const setDetails = Array.from({ length: ce.sets }, () => ({
          weight: '',
          reps: repMin,
          time: '',
        }));
        return {
          id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
          exercise: matched ?? ({ id: `coach-${ce.name}`, name: ce.name, body_part: '', is_custom: false } as any),
          weight: '',
          reps: repMin,
          sets: String(ce.sets),
          setDetails,
          isPR: false,
          videoUri: null,
          notes: ce.notes ? `Coach: ${ce.notes}` : '',
        };
      });
      setExerciseEntries(entries);

      // Pre-set rest timer from first exercise's rest prescription
      if (coachExercises[0]?.restSeconds && coachExercises[0].restSeconds > 0) {
        setRestTimerDefault(coachExercises[0].restSeconds);
      }
      setActiveView('log');
    } catch (err) {
      console.warn('[Record] Failed to parse coachDayJson:', err);
    }
  }, [coachDayJsonParam, exercises, coachDayLoaded]));

  useFocusEffect(useCallback(() => {
    if (params.videoUri && params.contextId && params.source === 'log') {
      const videoUri = params.videoUri as string;
      const exerciseKey = params.exerciseKey as string;
      const draftId = params.contextId as string;
      router.push({ pathname: '/(tabs)/record' });
      const draft = draftStore.getWorkoutDraft(draftId);
      if (draft) {
        const updatedEntries = draft.exerciseEntries.map((entry: any) => entry.id === exerciseKey ? { ...entry, videoUri } : entry);
        if (!updatedEntries.some((entry: any) => entry.id === exerciseKey)) { showAlert('Warning', 'Could not attach video to exercise. Please try again.'); return; }
        setExerciseEntries(updatedEntries);
        setWorkoutDate(new Date(draft.workoutDate));
        draftStore.clearWorkoutDraft(draftId);
      } else {
        setExerciseEntries((prev) => {
          const updated = prev.map((entry) => entry.id === exerciseKey ? { ...entry, videoUri } : entry);
          if (!updated.some((entry) => entry.id === exerciseKey)) { showAlert('Warning', 'Could not attach video to exercise. Please try again.'); return prev; }
          return updated;
        });
      }
    }
  }, [params.videoUri, params.contextId, params.source, params.exerciseKey]));

  const loadExercises = async () => {
    // Build local exercises from the comprehensive database
    const T = '1970-01-01T00:00:00.000Z';
    const localExercises: Exercise[] = EXERCISE_DATABASE.map((info) => {
      const id = `local-${info.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      const bodyPart = info.primaryMuscle === 'full_body' ? 'full_body'
        : info.primaryMuscle === 'arms' ? 'arms'
        : info.primaryMuscle;
      const isBaseline = normalizeToV1Lift(info.name) !== null;
      return { id, name: info.name, category: info.category, body_part: bodyPart, is_big_three: isBaseline, is_custom: false, created_at: T };
    });

    // Fetch Supabase exercises (custom + remote)
    const { data, error } = await supabase.from('exercises').select('*').eq('is_custom', false).order('name');
    const remoteExercises = (!error && Array.isArray(data) && data.length > 0) ? (data as Exercise[]) : [];

    // Merge: local first, then any remote exercises not already covered by name
    const localNames = new Set(localExercises.map((e) => e.name.toLowerCase()));
    const supplementary = remoteExercises.filter((e) => !localNames.has(e.name.toLowerCase()));
    setExercises([...localExercises, ...supplementary]);
    setLoadingExercises(false);
  };

  const loadEngagementPreferences = async () => {
    if (!profile?.id) return;
    const { data } = await supabase.from('engagement_preferences').select('workout_nudges_enabled, nudge_tone').eq('user_id', profile.id).maybeSingle();
    if (data) {
      setWorkoutNudgesEnabled(data.workout_nudges_enabled ?? true);
      setNudgeTone(data.nudge_tone as typeof nudgeTone || 'motivational');
    }
  };

  // Auto-load today's template
  useFocusEffect(useCallback(() => {
    if (loadingExercises || exercises.length === 0 || exerciseEntries.length > 0) return;
    const today = workoutDate.getDay();
    const template = templateStore[today];
    if (!template || template.exercises.length === 0) return;
    const loadKey = `${today}-${template.updatedAt}`;
    if (loadKey === lastTemplateLoadId) return;
    const newEntries: ExerciseEntry[] = template.exercises.map((te) => {
      const matched = exercises.find((e) => e.id === te.exerciseId) ?? null;
      return { id: Date.now().toString() + Math.random().toString(36).slice(2, 6), exercise: matched ?? ({ id: te.exerciseId, name: te.exerciseName, body_part: '', is_custom: false } as any), weight: '', reps: '', sets: '1', isPR: false, videoUri: null, notes: '' };
    });
    setExerciseEntries(newEntries);
    setLastTemplateLoadId(loadKey);
  }, [loadingExercises, exercises, exerciseEntries.length, templateStore, workoutDate, lastTemplateLoadId]));

  // Auto-save exercises back to the active template when exercises change
  useEffect(() => {
    if (activeTemplateKey == null || !profile?.id) return;
    const exercisesWithData = exerciseEntries.filter((e) => e.exercise);
    if (exercisesWithData.length === 0) return;
    const templateExercises: TemplateExercise[] = exercisesWithData.map((e) => ({ exerciseId: e.exercise!.id, exerciseName: e.exercise!.name }));
    const timer = setTimeout(async () => {
      await saveTemplate(activeTemplateKey, templateExercises, profile.id);
      setTemplateStore(await loadTemplates(profile.id));
    }, 1500);
    return () => clearTimeout(timer);
  }, [exerciseEntries, activeTemplateKey, profile?.id]);

  const handleSaveAsTemplate = async () => {
    const templateExercises: TemplateExercise[] = exerciseEntries.filter((e) => e.exercise).map((e) => ({ exerciseId: e.exercise!.id, exerciseName: e.exercise!.name }));
    if (templateExercises.length === 0) { showAlert('No Exercises', 'Add exercises first before saving a template.'); return; }
    const defaultName = DAY_SHORT[workoutDate.getDay()];
    if (Platform.OS === 'web') {
      const name = window.prompt('Template name:', defaultName);
      if (!name) return;
      await saveTemplate(name, templateExercises, profile?.id);
      setTemplateStore(await loadTemplates(profile?.id));
      setLastTemplateLoadId('');
      showAlert('Saved', `Template "${name}" saved!`);
      return;
    }
    await saveTemplate(defaultName, templateExercises, profile?.id);
    setTemplateStore(await loadTemplates(profile?.id));
    setLastTemplateLoadId('');
    showAlert('Saved', `Template saved as "${defaultName}"!`);
  };

  const handleSetLogged = useCallback(() => {
    // Don't auto-show timer - user taps the Rest button manually
  }, []);

  const selectExerciseForEntry = (entryId: string, bodyPart: string) => {
    setCurrentEntryId(entryId);
    setSelectedBodyPart(bodyPart);
    setShowExercisePicker(true);
  };

  const assignExerciseToEntry = (exercise: Exercise) => {
    if (currentEntryId) {
      updateExerciseEntry(currentEntryId, { exercise });
      setShowExercisePicker(false);
      setSelectedBodyPart(null);
      setCurrentEntryId(null);
    } else {
      // Create new exercise entry when no currentEntryId (e.g., opened from baseline gate)
      const newEntry: ExerciseEntry = {
        id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
        exercise,
        weight: '',
        reps: '',
        sets: '1',
        isPR: false,
        videoUri: null,
        notes: ''
      };
      setExerciseEntries(prev => [...prev, newEntry]);
      setShowExercisePicker(false);
      setSelectedBodyPart(null);
      setOpenedFromBaseline(false);
    }
  };

  const showVideoOptionsModal = (entryId: string) => { setCurrentEntryId(entryId); setShowVideoOptions(true); };

  const recordVideo = async () => {
    if (!currentEntryId) return;
    const currentEntry = exerciseEntries.find((entry) => entry.id === currentEntryId);
    if (!currentEntry || !currentEntry.exercise) { showAlert('Exercise Required', 'Please select an exercise before recording a video.'); setShowVideoOptions(false); return; }
    draftStore.setWorkoutDraft(workoutDraftId, { exerciseEntries, workoutDate: workoutDate.toISOString() });
    setShowVideoOptions(false);
    router.push({ pathname: '/record-video', params: { source: 'log', contextId: workoutDraftId, exerciseKey: currentEntryId } });
  };

  const startRecording = async () => {
    if (cameraRef && !isRecording) {
      setIsRecording(true);
      try {
        const video = await cameraRef.recordAsync({ maxDuration: 20 });
        if (currentEntryId && video.uri) updateExerciseEntry(currentEntryId, { videoUri: video.uri });
        setShowCamera(false); setIsRecording(false);
      } catch { setIsRecording(false); }
    }
  };

  const stopRecording = () => { if (cameraRef && isRecording) cameraRef.stopRecording(); };
  const closeCamera = () => { if (isRecording) stopRecording(); setShowCamera(false); setIsRecording(false); setCameraRef(null); };

  // Release camera when app goes to background (prevents iOS green dot)
  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'background' || state === 'inactive') {
        if (showCamera) {
          closeCamera();
        }
      }
    });
    return () => sub.remove();
  }, [showCamera]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePostToFeed = async () => {
    if (!savedSessionId) return;
    try {
      const { data: sessionLifts } = await supabase.from('lifts').select('*, exercises(*)').eq('session_id', savedSessionId);
      const totalVolume = sessionLifts?.reduce((sum, lift) => sum + (lift.volume || 0), 0) || 0;
      const exerciseNames = sessionLifts?.map((l: any) => l.exercises.name).join(', ') || '';
      await supabase.from('posts').insert({ user_id: profile!.id, content: `Workout Logged: ${exerciseNames.substring(0, 100)}... Total volume: ${totalVolume}${profile?.units || 'lbs'}`, post_type: 'workout', is_competition: false });
      Alert.alert('Posted', 'Lift Posted');
      setShowPostOption(false); setSavedSessionId(null);
    } catch { Alert.alert('Oops', "Couldn't share your lift. Please try again."); }
  };

  const handleSkipPost = () => { setShowPostOption(false); setSavedSessionId(null); };

  const performWorkoutSave = async () => {
    setLoading(true);
    if (!profile) { showAlert('Error', 'User profile not found'); setLoading(false); return; }

    const sessionCheck = validateSessionSize(exerciseEntries.length);
    if (!sessionCheck.valid) { showAlert('Too Many Exercises', sessionCheck.errors.join(' ')); setLoading(false); return; }
    const dateCheck = validateWorkoutDate(workoutDate);
    if (!dateCheck.valid) { showAlert('Invalid Date', dateCheck.errors.join(' ')); setLoading(false); return; }

    const weightUnit = (String(profile?.units || '').toLowerCase() === 'kg' ? 'kg' : 'lb') as 'lb' | 'kg';
    for (const entry of exerciseEntries) {
      if (!entry.exercise) continue;
      const effectiveWeight = entry.weight === '' ? '0' : entry.weight;
      const effectiveReps = entry.mode === 'time' ? (entry.duration || '1') : (entry.reps === '' ? '0' : entry.reps);
      if (effectiveReps !== '0') {
        const v = validateExerciseEntry(effectiveWeight, effectiveReps, entry.sets || '1', weightUnit);
        if (!v.valid) { showAlert('Invalid Entry', `${entry.exercise.name}: ${v.errors.join(' ')}`); setLoading(false); return; }
      }
    }

    const entryKeys = [...new Set(exerciseEntries.filter((e) => e.exercise).map((e) => `${e.exercise!.id}:${e.weight}:${e.reps}:${e.sets}`))];
    const hasDuplicate = entryKeys.some((key) => isDuplicateSubmission(key));
    if (hasDuplicate) { showAlert('Duplicate Detected', 'This workout was just submitted. Please wait a moment before resubmitting.'); setLoading(false); return; }

    try {
      const result = await saveWorkout(exerciseEntries, profile, {
        workoutDate,
        workoutName,
        sessionNotes,
        supersetGroups,
        authUserId: session?.user?.id ?? profile.id,
        refreshProfile,
      });

      if (result.newMilestones.length > 0) setMilestonesToShow(result.newMilestones);
      if (result.badgesUnlocked.length > 0) setBadgesToShow(result.badgesUnlocked);
      setSavedSessionId(result.workoutId);
      setShowPostOption(false);

      // Detect PRs: compare each exercise's best set against previous strength_events
      let prSet: Record<string, number> = {};
      try {
        const exerciseNames = exerciseEntries.filter(e => e.exercise).map(e => e.exercise!.name);
        if (exerciseNames.length > 0 && profile?.id) {
          const { data: prevBests } = await supabase
            .from('strength_events')
            .select('canonical_name,weight_kg')
            .eq('profile_id', profile.id)
            .order('weight_kg', { ascending: false });
          const bestByName: Record<string, number> = {};
          for (const row of prevBests ?? []) {
            const n = (row.canonical_name || '').toLowerCase();
            if (!bestByName[n] || row.weight_kg > bestByName[n]) bestByName[n] = row.weight_kg;
          }
          // Check current entries against previous bests (excluding this workout since events already inserted)
          for (const e of exerciseEntries) {
            if (!e.exercise) continue;
            const w = parseFloat(e.weight) || 0;
            const key = e.exercise.name.toLowerCase().replace(/\s+/g, '-');
            // A PR if weight exceeds previous best (strength_events inserted before this point may include current,
            // so we check if this is the new max - at minimum 2 occurrences means it existed before)
            const prevBest = bestByName[key] ?? 0;
            if (w > 0 && w >= prevBest) prSet[e.exercise.name] = w;
          }
        }
      } catch { /* PR detection is best-effort */ }

      // Upload video proofs for exercises that have video
      try {
        const userId = session?.user?.id ?? profile.id;
        for (const entry of exerciseEntries) {
          if (!entry.videoUri || !entry.exercise) continue;
          const liftKey = entry.exercise.name.toLowerCase().replace(/\s+/g, '-');
          // Find the most recent strength_event for this lift
          const { data: events } = await supabase
            .from('strength_events')
            .select('id')
            .eq('profile_id', userId)
            .eq('lift_key', liftKey)
            .order('performed_at', { ascending: false })
            .limit(1);
          if (events?.[0]) {
            const videoLimit = await checkVideoLimit(userId, tier);
            if (!videoLimit.allowed) {
              log.warn('Video limit reached:', videoLimit.error);
            } else {
              const { url } = await uploadLiftVideo(userId, entry.videoUri);
              if (url) await attachVideoToEvent(events[0].id, url);
            }
          }
        }
      } catch (videoErr) {
        log.warn('Video upload failed (non-blocking):', videoErr);
      }

      // Capture exercise data for share card — best set (heaviest weight), with set count
      const shareData: WorkoutExercise[] = exerciseEntries
        .filter((e) => e.exercise)
        .map((e) => {
          const sets = e.setDetails && e.setDetails.length > 0 ? e.setDetails : [{ weight: e.weight, reps: e.reps }];
          // Best set = heaviest weight; if tie, most reps
          const bestSet = sets.reduce((best: any, s: any) => {
            const w = parseFloat(s.weight) || 0;
            const r = parseInt(s.reps, 10) || 0;
            const bw = parseFloat(best.weight) || 0;
            const br = parseInt(best.reps, 10) || 0;
            return w > bw || (w === bw && r > br) ? s : best;
          }, sets[0]);
          return {
            name: e.exercise!.name,
            weight: parseFloat(bestSet.weight) || 0,
            reps: parseInt(bestSet.reps, 10) || 0,
            sets: sets.length,
            isPR: e.exercise!.name in prSet,
          };
        });
      setShareExercises(shareData);

      // Record submission timestamps so the duplicate guard fires on rapid re-saves
      recordSubmissions(entryKeys);

      // Stash entries for post-save summary stats before resetting
      (window as any).__lastSavedEntries = [...exerciseEntries];
      resetForm();
      setJustSaved(true);

      // Workout completion confetti (every save)
      setShowSaveConfetti(true);
      setTimeout(() => setShowSaveConfetti(false), 2500);

      // First workout celebration
      try {
        const uid = session?.user?.id ?? profile.id;
        const firstCelebrated = await AsyncStorage.getItem(`duel:first_workout_celebrated:${uid}`);
        if (!firstCelebrated) {
          await AsyncStorage.setItem(`duel:first_workout_celebrated:${uid}`, '1');
          showAlert('🎉 First Workout Logged!', 'Your strength journey begins.');
        } else {
          showAlert('Workout Saved 💪', 'Keep pushing!');
        }
      } catch {
        showAlert('Recorded', 'Lift Recorded');
      }

      if (shareData.length > 0) setShowShareModal(true);
      setActiveView('logs');

      // App Store review prompt: fire after 3rd workout, once only
      try {
        const uid = session?.user?.id ?? profile.id;
        const countKey = `duel:workout_count:${uid}`;
        const raw = await AsyncStorage.getItem(countKey);
        const prevCount = parseInt(raw ?? '0', 10) || 0;
        const newCount = prevCount + 1;
        await AsyncStorage.setItem(countKey, String(newCount));
        if (newCount === 3) {
          if (await StoreReview.hasAction()) {
            await StoreReview.requestReview();
          }
        }
      } catch (reviewErr) {
        log.warn('StoreReview prompt failed (non-blocking):', reviewErr);
      }

      // Duel opponent notification is handled in workoutSave.ts - no duplicate here.

      // Check if we should show the visibility opt-in modal
      try {
        const uid = session?.user?.id ?? profile.id;
        // Clear baseline cache so the fresh status is fetched after saving a workout
        clearBaselineCache(uid);
        // Re-fetch and update the banner state immediately
        fetchBaselineStatus(uid).then((s) => setBaselineComplete(s?.isComplete ?? false)).catch(() => {});
        const alreadyShown = await AsyncStorage.getItem(`duel:visibility_shown:${uid}`);
        if (!alreadyShown && !profile.visibility_opted_in) {
          const baseline = await fetchBaselineStatus(uid);
          if (baseline.isComplete) {
            setShowVisibilityModal(true);
          }
        }
      } catch (e) {
        log.warn('[VisibilityOptIn] Check failed:', e);
      }
    } catch (error: any) {
      log.error('Failed to save workout:', error);
      captureError(error, { context: 'record.performWorkoutSave' });
      showAlert('Error', error.message || 'Failed to save workout');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (exerciseEntries.length === 0) { showAlert('Error', 'Please add at least one exercise'); return; }
    const invalidEntries = exerciseEntries.filter((entry) => !entry.exercise);
    if (invalidEntries.length > 0) { showAlert('Error', 'Please select an exercise for each entry.'); return; }
    if (!profile?.id) { showAlert('Error', 'You must be logged in to save a workout'); return; }
    const emptyExercises = exerciseEntries.filter((entry) => !entry.sets || entry.sets === '0' || entry.sets === '');
    if (emptyExercises.length > 0 && workoutNudgesEnabled) { setShowEmptyExerciseWarning(true); return; }
    await performWorkoutSave();
  };

  if (loadingExercises && activeView === 'log') {
    return (<View style={[styles.centerContainer, { backgroundColor: theme.colors.background, padding: 24 }]}><SkeletonRow rows={5} /></View>);
  }

  const renderLogWorkout = () => (
    <>
      <LinearGradient colors={[theme.colors.background, theme.colors.surfaceVariant]} style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <TouchableOpacity
            style={styles.dateButton}
            onPress={() => {
              const today = new Date();
              const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
              const twoDaysAgo = new Date(today); twoDaysAgo.setDate(today.getDate() - 2);
              Alert.alert('Workout Date', 'Select the date of this workout', [
                { text: 'Today', onPress: () => setWorkoutDate(today) },
                { text: 'Yesterday', onPress: () => setWorkoutDate(yesterday) },
                { text: '2 Days Ago', onPress: () => setWorkoutDate(twoDaysAgo) },
                { text: 'Cancel', style: 'cancel' },
              ]);
            }}
            accessibilityRole="button"
            accessibilityLabel="Change workout date"
          >
            <Calendar size={16} color={theme.colors.textSecondary} />
            <Text style={[styles.dateHeaderText, { color: theme.colors.textSecondary }]} numberOfLines={1}>{workoutDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</Text>
          </TouchableOpacity>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {/* WorkoutTimer hidden from header — duration tracked silently and shown on save */}
            <TouchableOpacity onPress={() => setShowRestTimer(true)} hitSlop={8} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: theme.colors.accent + '20', borderColor: theme.colors.accent, borderWidth: 1, borderRadius: 0, paddingVertical: 8, paddingHorizontal: 12 }} accessibilityRole="button" accessibilityLabel="Rest Timer">
              <Timer size={14} color={theme.colors.accent} /><Text style={{ fontSize: 12, fontWeight: '700', color: theme.colors.accent }}>Rest</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => {
                // Pre-fill with last weight from the focused/last exercise
                const lastEntry = exerciseEntries[exerciseEntries.length - 1];
                const lastSet = lastEntry?.setDetails?.[lastEntry.setDetails.length - 1];
                const prefill = lastSet?.weight ? Number(lastSet.weight) : undefined;
                setPlateCalcWeight(prefill);
                setShowPlateCalc(true);
              }} hitSlop={8} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: theme.colors.accent + '20', borderColor: theme.colors.accent, borderWidth: 1, borderRadius: 0, paddingVertical: 8, paddingHorizontal: 12 }} accessibilityRole="button" accessibilityLabel="Plate Calculator">
              <Calculator size={14} color={theme.colors.accent} /><Text style={{ fontSize: 12, fontWeight: '700', color: theme.colors.accent }}>Plates</Text>
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled">
        {baselineComplete === false && !baselinePromptDismissed && exerciseEntries.length === 0 && !lastWorkoutDate && !(profile?.duel_standard_strength_kg && profile.duel_standard_strength_kg > 0) && (
          <View style={[styles.baselinePrompt, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
            <Text style={[styles.baselinePromptTitle, { color: theme.colors.text }]}>Complete Your Strength Baseline</Text>
            <Text style={[styles.baselinePromptDesc, { color: theme.colors.textSecondary }]}>Log all 5 lifts to unlock your Strength Score and start competing.</Text>
            <TouchableOpacity style={[styles.baselineLoadBtn, { backgroundColor: theme.colors.accent }]} onPress={() => { loadBaselineEntries(); setBaselinePromptDismissed(true); }}>
              <Text style={[styles.baselineLoadBtnText, { color: theme.colors.accentForeground }]}>Load Baseline Workout</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setBaselinePromptDismissed(true)}><Text style={[styles.baselineDismiss, { color: theme.colors.textTertiary }]}>Skip for now</Text></TouchableOpacity>
          </View>
        )}

        {justSaved && exerciseEntries.length === 0 && (() => {
          // Compute workout stats for the summary
          const durationSec = workoutStartTime ? Math.floor((Date.now() - workoutStartTime) / 1000) : 0;
          const durationMin = Math.floor(durationSec / 60);
          const savedEntries = (window as any).__lastSavedEntries ?? [];
          const totalSets = savedEntries.reduce((sum: number, e: any) => sum + (e.setDetails?.length ?? (parseInt(e.sets) || 0)), 0);
          const totalVol = savedEntries.reduce((sum: number, e: any) => {
            const sets = e.setDetails ?? [{ weight: e.weight, reps: e.reps }];
            return sum + sets.reduce((s: number, set: any) => s + (parseFloat(set.weight) || 0) * (parseInt(set.reps) || 0), 0);
          }, 0);
          return (
            <View style={{ padding: 16, backgroundColor: theme.colors.accent + '15', marginBottom: 16, borderRadius: 4, borderWidth: 1, borderColor: theme.colors.accent + '40' }}>
              <Text style={{ color: theme.colors.accent, fontWeight: '900', textAlign: 'center', fontSize: 16, letterSpacing: 1 }}>
                WORKOUT COMPLETE ✅
              </Text>
              <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginTop: 12 }}>
                {durationMin > 0 && (
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ color: theme.colors.text, fontWeight: '800', fontSize: 20 }}>{durationMin}m</Text>
                    <Text style={{ color: theme.colors.textTertiary, fontSize: 11 }}>DURATION</Text>
                  </View>
                )}
                {savedEntries.length > 0 && (
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ color: theme.colors.text, fontWeight: '800', fontSize: 20 }}>{savedEntries.length}</Text>
                    <Text style={{ color: theme.colors.textTertiary, fontSize: 11 }}>EXERCISES</Text>
                  </View>
                )}
                {totalSets > 0 && (
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ color: theme.colors.text, fontWeight: '800', fontSize: 20 }}>{totalSets}</Text>
                    <Text style={{ color: theme.colors.textTertiary, fontSize: 11 }}>SETS</Text>
                  </View>
                )}
                {totalVol > 0 && (
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ color: theme.colors.text, fontWeight: '800', fontSize: 20 }}>{totalVol >= 1000 ? `${Math.round(totalVol / 1000)}K` : Math.round(totalVol)}</Text>
                    <Text style={{ color: theme.colors.textTertiary, fontSize: 11 }}>VOLUME</Text>
                  </View>
                )}
              </View>
            </View>
          );
        })()}

        <TextInput style={[styles.workoutNameInput, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]} value={workoutName} onChangeText={setWorkoutName} placeholder={`Workout - ${workoutDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`} placeholderTextColor={theme.colors.textTertiary} maxLength={100} accessibilityLabel="Workout name" />

        {exerciseEntries.map((entry, index) => {
          const isInSuperset = !!entry.supersetGroupId;
          const prevEntry = index > 0 ? exerciseEntries[index - 1] : null;
          const nextEntry = index < exerciseEntries.length - 1 ? exerciseEntries[index + 1] : null;
          const isFirstInGroup = isInSuperset && (!prevEntry || prevEntry.supersetGroupId !== entry.supersetGroupId);
          const isLastInGroup = isInSuperset && (!nextEntry || nextEntry.supersetGroupId !== entry.supersetGroupId);
          return (
            <View key={entry.id} style={isInSuperset ? styles.supersetWrapper : undefined}>
              {isInSuperset && (<View style={[styles.supersetBar, { backgroundColor: theme.colors.accent, borderTopLeftRadius: isFirstInGroup ? 4 : 0, borderBottomLeftRadius: isLastInGroup ? 4 : 0 }]} />)}
              <View style={isInSuperset ? styles.supersetContent : undefined}>
                {isFirstInGroup && (<Text style={[styles.supersetLabel, { color: theme.colors.accent }]}>SUPERSET</Text>)}
                <ExerciseEntryCard entry={entry} index={index} bodyPartGridWidth={bodyPartGridWidth} onBodyPartGridLayout={setBodyPartGridWidth} onRemove={(id) => setConfirmRemoveId(id)} onUpdate={updateExerciseEntry} onSelectBodyPart={selectExerciseForEntry} onShowVideoOptions={showVideoOptionsModal} onSetLogged={handleSetLogged} totalExercises={exerciseEntries.length} onMoveUp={() => reorderExercise(index, index - 1)} onMoveDown={() => reorderExercise(index, index + 1)} />
              </View>
            </View>
          );
        })}

        <View style={styles.actionRow}>
          <TouchableOpacity style={[styles.addExerciseButton, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.accent, flex: 1 }]} onPress={() => { setJustSaved(false); addExerciseEntry(); }} accessibilityRole="button" accessibilityLabel="Add Exercise">
            <Plus size={24} color={theme.colors.accent} /><Text style={[styles.addExerciseText, { color: theme.colors.accent }]}>Add Exercise</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.addSupersetButton, { backgroundColor: supersetMode ? theme.colors.accent : theme.colors.surfaceVariant, borderColor: theme.colors.accent }]} onPress={toggleSupersetMode} accessibilityRole="button" accessibilityLabel={supersetMode ? 'End Superset' : 'Start Superset'}>
            <Link size={20} color={supersetMode ? theme.colors.accentForeground : theme.colors.accent} /><Text style={[styles.addSupersetText, { color: supersetMode ? theme.colors.accentForeground : theme.colors.accent }]}>{supersetMode ? 'End SS' : 'Superset'}</Text>
          </TouchableOpacity>
        </View>

        {/* DUEL Program button — Pro+ with a coach plan only */}
        {exerciseEntries.length === 0 && hasAtLeast(tier, 'pro') && coachPlan !== null && (
          <TouchableOpacity
            style={[styles.loadBaselineBtn, { borderColor: theme.colors.accent, borderWidth: 2, marginBottom: 8, backgroundColor: theme.colors.accent + '12' }]}
            onPress={() => router.push('/duel-coach-plan' as any)}
            accessibilityRole="button"
            accessibilityLabel="Open DUEL Program">
            <Text style={[styles.loadBaselineBtnText, { color: theme.colors.accent, fontWeight: '800' }]}>{'🏋️ DUEL PROGRAM'}</Text>
          </TouchableOpacity>
        )}

        {exerciseEntries.length === 0 && profile?.id && lastWorkoutDate && (
          <TouchableOpacity
            style={[styles.loadBaselineBtn, { borderColor: theme.colors.accent, borderWidth: 1, marginBottom: 8 }]}
            onPress={() => setShowTemplateModal(true)}>
            <Text style={[styles.loadBaselineBtnText, { color: theme.colors.accent, fontWeight: '700' }]}>📋 TEMPLATES</Text>
          </TouchableOpacity>
        )}

        {exerciseEntries.length === 0 && profile?.id && lastWorkoutDate && (
          <TouchableOpacity style={[styles.loadBaselineBtn, { borderColor: theme.colors.accent, borderWidth: 1, marginBottom: 8 }]} disabled={loadingRepeat} onPress={async () => {
            setLoadingRepeat(true);
            try {
              const entries = await fetchLastWorkoutEntries(profile.id, exercises);
              if (entries && entries.length > 0) setExerciseEntries(entries);
              else showAlert('No Previous Workout', 'No workout history found to repeat.');
            } catch { showAlert('Error', 'Failed to load last workout.'); } finally { setLoadingRepeat(false); }
          }}>
            <Text style={[styles.loadBaselineBtnText, { color: theme.colors.accent, fontWeight: '700' }]}>{loadingRepeat ? 'Loading...' : `REPEAT ${lastWorkoutDate.toLocaleDateString(undefined, { weekday: 'long' }).toUpperCase()} WORKOUT`}</Text>
          </TouchableOpacity>
        )}

        {exerciseEntries.length === 0 && lastWorkoutDate && (
          <TouchableOpacity style={[styles.loadBaselineBtn, { borderColor: theme.colors.border }]} onPress={loadBaselineEntries}>
            <Text style={[styles.loadBaselineBtnText, { color: theme.colors.textSecondary }]}>Load Strength Baseline (5 lifts)</Text>
          </TouchableOpacity>
        )}

        {/* New user (zero workouts): single prominent Start button */}
        {exerciseEntries.length === 0 && profile?.id && !lastWorkoutDate && (
          <AnimatedButton
            style={[styles.startWorkoutBtn, { backgroundColor: theme.colors.accent }]}
            onPress={() => { setJustSaved(false); addExerciseEntry(); }}
            accessibilityRole="button"
            accessibilityLabel="Start a Workout">
            <Dumbbell size={24} color={theme.colors.accentForeground} />
            <Text style={[styles.startWorkoutText, { color: theme.colors.accentForeground }]}>Start a Workout</Text>
          </AnimatedButton>
        )}

        {/* Newcomer tip card */}
        {stage === 'newcomer' && exerciseEntries.length === 0 && (
          <View style={[styles.newcomerTip, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
            <Text style={[styles.newcomerTipTitle, { color: theme.colors.accent }]}>💡 GET YOUR STRENGTH SCORE</Text>
            <Text style={[styles.newcomerTipDesc, { color: theme.colors.textSecondary }]}>
              Start with your Core 5 lifts: Bench Press, Back Squat, Deadlift, Weighted Pull-Up, Dead Hang
            </Text>
          </View>
        )}

        {exerciseEntries.length > 0 && exerciseEntries.some((e) => e.exercise) && (
          hasAtLeast(tier, 'pro') ? (
            <TouchableOpacity style={[styles.saveTemplateBtn, { borderColor: theme.colors.border }]} onPress={handleSaveAsTemplate}>
              <Text style={[styles.saveTemplateText, { color: theme.colors.textSecondary }]}>Save as {DAY_SHORT[workoutDate.getDay()]} Template</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={[styles.saveTemplateBtn, { borderColor: theme.colors.border, opacity: 0.5 }]} onPress={() => router.push('/paywall')}>
              <Text style={[styles.saveTemplateText, { color: theme.colors.textSecondary }]}>Save as Template - Pro Feature 🔒</Text>
            </TouchableOpacity>
          )
        )}

        {exerciseEntries.length > 0 && (
          <TouchableOpacity style={[styles.newWorkoutBtn, { borderColor: theme.colors.border }]} onPress={() => { setExerciseEntries([]); setLastTemplateLoadId(''); setActiveTemplateKey(null); hapticsUtil.workoutSaved(); }}>
            <Text style={[styles.saveTemplateText, { color: theme.colors.textSecondary }]}>Start New Workout</Text>
          </TouchableOpacity>
        )}

        {exerciseEntries.length > 0 && (
          <TextInput style={[styles.sessionNotesInput, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]} value={sessionNotes} onChangeText={setSessionNotes} placeholder="Session notes (optional)" placeholderTextColor={theme.colors.textTertiary} multiline maxLength={1000} accessibilityLabel="Session notes" />
        )}

        {exerciseEntries.length > 0 && (
          <Pressable
            style={[styles.submitButton, { backgroundColor: theme.colors.accent }, loading && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={loading}
            accessibilityRole="button"
            accessibilityLabel="Save Workout"
            android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
            hitSlop={{ top: 8, bottom: 8 }}>
            {loading ? <ActivityIndicator color={theme.colors.accentForeground} /> : <Text style={[styles.submitButtonText, { color: theme.colors.accentForeground }]} numberOfLines={1}>Save Workout</Text>}
          </Pressable>
        )}
      </ScrollView>

      <MobileExercisePicker visible={showExercisePicker} onClose={() => { setShowExercisePicker(false); setSelectedBodyPart(null); setCurrentEntryId(null); setExerciseSearch(''); setOpenedFromBaseline(false); }} onSelectExercise={assignExerciseToEntry} exercises={exercises} recentExercises={[]} initialSelectedBodyPart={selectedBodyPart || undefined} focusCanonical={openedFromBaseline} />

      <EmptyExerciseWarning visible={showEmptyExerciseWarning} onClose={() => setShowEmptyExerciseWarning(false)} onFinish={() => setShowEmptyExerciseWarning(false)} onRemove={() => {
        setExerciseEntries(exerciseEntries.filter((entry) => entry.sets && entry.sets !== '0' && entry.sets !== ''));
        setShowEmptyExerciseWarning(false);
        showAlert('Removed', 'Exercises Removed');
      }} onSaveAnyway={async () => {
        setShowEmptyExerciseWarning(false);
        const originalEntries = [...exerciseEntries];
        setExerciseEntries(exerciseEntries.filter((entry) => entry.sets && entry.sets !== '0' && entry.sets !== ''));
        try { await performWorkoutSave(); } catch { setExerciseEntries(originalEntries); }
      }} emptyCount={exerciseEntries.filter((entry) => !entry.sets || entry.sets === '0' || entry.sets === '').length} nudgeTone={nudgeTone} />
    </>
  );

  function loadBaselineEntries() {
    const baselineEntries: ExerciseEntry[] = [];
    for (const liftKey of V1_LIFT_POOL) {
      const label = V1_LIFT_LABELS[liftKey];
      const match = exercises.find((e) => e.name.toLowerCase() === label.toLowerCase()) || exercises.find((e) => normalizeToV1Lift(e.name) === liftKey);
      if (match) baselineEntries.push({ id: Date.now().toString() + Math.random().toString(36).slice(2, 6), exercise: match, weight: '', reps: '', sets: '1', isPR: false, videoUri: null, notes: '' });
    }
    setExerciseEntries(baselineEntries);
  }

  return (
    <ProfileGate requiredFields={['handle', 'display_name']}>
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
    <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
      {/* Nav header rendered above tutorial overlay so tabs always remain tappable */}
      <View style={{ zIndex: 10000 }}>
        <RecordNavHeader activeView={activeView} onChangeView={setActiveView} />
      </View>
      {activeView === 'log' && (
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
          {renderLogWorkout()}
        </KeyboardAvoidingView>
      )}
      {activeView === 'logs' && <WorkoutLogs />}
      {activeView === 'analytics' && <Analytics />}
      {activeView === 'program' && (
        coachPlan ? (
          // Has a DUEL Coach plan — show it inline with a link to full plan
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.border }}>
              <Text style={{ color: theme.colors.text, fontWeight: '800', fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>DUEL Program — Week {coachPlan.weekNumber}</Text>
              <Pressable onPress={() => router.push('/duel-coach-plan' as any)} android_ripple={{ color: theme.colors.accent + '20', borderless: false }}>
                <Text style={{ color: theme.colors.accent, fontSize: 13, fontWeight: '700' }}>Full Plan →</Text>
              </Pressable>
            </View>
            <ScrollView contentContainerStyle={{ padding: 16, gap: 8 }}>
              <Text style={{ color: theme.colors.textSecondary, fontSize: 13, lineHeight: 18, marginBottom: 8 }}>{coachPlan.adaptationNote?.split('.')[0]}.</Text>
              {coachPlan.days.map((day, idx) => (
                <Pressable
                  key={idx}
                  style={{ backgroundColor: theme.colors.surface, borderRadius: 2, borderWidth: 1, borderColor: theme.colors.border, padding: 14 }}
                  onPress={() => router.push({ pathname: '/(tabs)/record', params: { coachDayJson: JSON.stringify(day.exercises.map(ex => ({ name: ex.name, sets: ex.sets, reps: ex.reps, restSeconds: ex.restSeconds, rpe: ex.rpe, notes: ex.notes }))) } } as any)}
                  android_ripple={{ color: theme.colors.accent + '15', borderless: false }}
                >
                  <Text style={{ color: theme.colors.accent, fontWeight: '700', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{day.dayLabel}</Text>
                  <Text style={{ color: theme.colors.textSecondary, fontSize: 12, marginTop: 2 }}>{day.focus} · {day.exercises.length} exercises · ~{day.estimatedMinutes}min</Text>
                  <Text style={{ color: theme.colors.textTertiary, fontSize: 11, marginTop: 6 }}>Tap to load into workout →</Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        ) : (
          <TodaysProgram />
        )
      )}
      <DevStrengthPanel devSeCount={devSeCount} devSeRows={devSeRows} devSeLoading={devSeLoading} devPingResult={devPingResult} devPingLoading={devPingLoading} onQueryStrengthEvents={loadDevStrengthEvents} onPingFunction={pingStrengthEventsFunction} />
      <PostOptionModal visible={showPostOption} onPost={handlePostToFeed} onSkip={handleSkipPost} />
      <WorkoutShareModal
        visible={showShareModal}
        onClose={() => setShowShareModal(false)}
        username={profile?.handle ?? profile?.display_name ?? 'athlete'}
        gymName={profile?.home_gym_name ?? 'My Gym'}
        exercises={shareExercises}
      />
      <VideoOptionsModal visible={showVideoOptions} onRecord={recordVideo} onClose={() => setShowVideoOptions(false)} />
      <CameraModal visible={showCamera} isRecording={isRecording} onCameraRef={setCameraRef} onStartRecording={startRecording} onStopRecording={stopRecording} onClose={closeCamera} />
      <RestTimer isVisible={showRestTimer} onDismiss={() => setShowRestTimer(false)} defaultSeconds={restTimerDefault} />
      <PlateCalculator visible={showPlateCalc} onClose={() => setShowPlateCalc(false)} initialWeight={plateCalcWeight} unit={(String(profile?.units || '').toLowerCase() === 'kg' ? 'kg' : 'lb') as 'lb' | 'kg'} />
      {activeView === 'log' && <TutorialOverlay screenKey="record" />}
      <Confetti active={showSaveConfetti} />
      {milestonesToShow.length > 0 && (<MilestoneBanner milestone={milestonesToShow[0]} onDismiss={() => setMilestonesToShow((prev) => prev.slice(1))} />)}
      {badgesToShow.length > 0 && (
        <BadgeCelebration
          badge={badgesToShow[0] ? {
            id: badgesToShow[0].badge_key ?? badgesToShow[0].id ?? '',
            name: badgesToShow[0].badge_label ?? badgesToShow[0].name ?? 'Badge',
            description: badgesToShow[0].badge_description ?? badgesToShow[0].description ?? '',
            icon_name: badgesToShow[0].badge_emoji ?? '🏆',
            tier: badgesToShow[0].category ?? 'achievement',
          } : null}
          visible={badgesToShow.length > 0}
          onClose={() => setBadgesToShow((prev) => prev.slice(1))}
        />
      )}
      <VisibilityOptInModal visible={showVisibilityModal} userId={profile?.id ?? ''} onComplete={() => { setShowVisibilityModal(false); if (refreshProfile) refreshProfile(); }} />

      {/* Template Picker Modal */}
      <Modal visible={showTemplateModal} transparent animationType="slide">
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: theme.colors.surface, borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingTop: 16, paddingBottom: 40, maxHeight: '70%' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, marginBottom: 16 }}>
              <Text style={{ fontSize: 18, fontWeight: '700', color: theme.colors.text }}>Templates</Text>
              <TouchableOpacity onPress={() => setShowTemplateModal(false)}>
                <Text style={{ fontSize: 15, color: theme.colors.accent, fontWeight: '600' }}>Close</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={{ paddingHorizontal: 20 }}>
              {/* Today's template */}
              {(() => {
                const todayIdx = workoutDate.getDay();
                const todayTemplate = templateStore[todayIdx];
                const isPro = hasAtLeast(tier, 'pro');
                return todayTemplate && todayTemplate.exercises.length > 0 ? (
                  <TouchableOpacity
                    style={{ backgroundColor: theme.colors.background, borderRadius: 2, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: theme.colors.accent }}
                    onPress={() => {
                      if (!isPro) { router.push('/paywall'); setShowTemplateModal(false); return; }
                      const newEntries: ExerciseEntry[] = todayTemplate.exercises.map((te: { exerciseId: string; exerciseName: string }) => {
                        const matched = exercises.find((e) => e.id === te.exerciseId) ?? null;
                        return { id: Date.now().toString() + Math.random().toString(36).slice(2, 6), exercise: matched ?? ({ id: te.exerciseId, name: te.exerciseName, body_part: '', is_custom: false } as any), weight: '', reps: '', sets: '1', isPR: false, videoUri: null, notes: '' };
                      });
                      setExerciseEntries(newEntries);
                      setActiveTemplateKey(todayIdx);
                      setShowTemplateModal(false);
                    }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: theme.colors.accent }}>📅 {DAY_SHORT[todayIdx]} Template</Text>
                    <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 4 }}>{todayTemplate.exercises.map((e: any) => e.exerciseName).join(', ')}</Text>
                    {!isPro && <Text style={{ fontSize: 11, color: theme.colors.textTertiary, marginTop: 2 }}>🔒 Pro</Text>}
                  </TouchableOpacity>
                ) : null;
              })()}

              {/* Free tier info banner */}
              {!hasAtLeast(tier, 'pro') && (
                <View style={{ backgroundColor: theme.colors.surfaceVariant, borderRadius: 2, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: theme.colors.border }}>
                  <Text style={{ fontSize: 12, color: theme.colors.textSecondary }}>✅ Push Day is free  ·  🔒 Other templates require Pro</Text>
                </View>
              )}

              {/* PPL Templates */}
              {[
                { label: 'Push Day - Chest, Shoulders & Triceps', key: 'push', icon: '💪', free: true },
                { label: 'Pull Day - Back & Biceps', key: 'pull', icon: '🏋️', free: false },
                { label: 'Leg Day - Quads, Hamstrings & Glutes', key: 'legs', icon: '🦵', free: false },
              ].map((t) => {
                const isPro = hasAtLeast(tier, 'pro');
                const canUse = isPro || t.free;
                const tmpl = templateStore[t.key];
                return (
                  <TouchableOpacity
                    key={t.key}
                    style={{ backgroundColor: theme.colors.background, borderRadius: 2, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: canUse ? theme.colors.border : theme.colors.border, opacity: canUse ? 1 : 0.7 }}
                    onPress={() => {
                      if (!canUse) {
                        Alert.alert('Pro Feature', 'Upgrade to Pro to unlock all workout templates.', [
                          { text: 'Not Now', style: 'cancel' },
                          { text: 'View Plans', onPress: () => { router.push('/paywall'); setShowTemplateModal(false); } },
                        ]);
                        return;
                      }
                      if (tmpl && tmpl.exercises.length > 0) {
                        const newEntries: ExerciseEntry[] = tmpl.exercises.map((te: { exerciseId: string; exerciseName: string }) => {
                          const matched = exercises.find((e) => e.id === te.exerciseId) ?? null;
                          return { id: Date.now().toString() + Math.random().toString(36).slice(2, 6), exercise: matched ?? ({ id: te.exerciseId, name: te.exerciseName, body_part: '', is_custom: false } as any), weight: '', reps: '', sets: '1', isPR: false, videoUri: null, notes: '' };
                        });
                        setExerciseEntries(newEntries);
                      } else {
                        addExerciseEntry();
                      }
                      setActiveTemplateKey(t.key);
                      setShowTemplateModal(false);
                    }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Text style={{ fontSize: 15, fontWeight: '700', color: theme.colors.text }}>{t.icon} {t.label}</Text>
                      {!canUse && <Text style={{ fontSize: 11, color: theme.colors.accent, fontWeight: '700' }}>PRO</Text>}
                      {t.free && !isPro && <Text style={{ fontSize: 11, color: '#22c55e', fontWeight: '700' }}>FREE</Text>}
                    </View>
                    {tmpl && tmpl.exercises.length > 0 && (
                      <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 4 }}>{tmpl.exercises.map((e: any) => e.exerciseName).join(', ')}</Text>
                    )}
                  </TouchableOpacity>
                );
              })}

              {/* Custom templates from store */}
              {Object.entries(templateStore).filter(([key]) => {
                const isDay = !isNaN(Number(key));
                const isPPL = ['push', 'pull', 'legs'].includes(key);
                return !isDay && !isPPL;
              }).map(([key, tmpl]) => (
                <TouchableOpacity
                  key={key}
                  style={{ backgroundColor: theme.colors.background, borderRadius: 2, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: theme.colors.border }}
                  onPress={() => {
                    const newEntries: ExerciseEntry[] = tmpl.exercises.map((te: { exerciseId: string; exerciseName: string }) => {
                      const matched = exercises.find((e) => e.id === te.exerciseId) ?? null;
                      return { id: Date.now().toString() + Math.random().toString(36).slice(2, 6), exercise: matched ?? ({ id: te.exerciseId, name: te.exerciseName, body_part: '', is_custom: false } as any), weight: '', reps: '', sets: '1', isPR: false, videoUri: null, notes: '' };
                    });
                    setExerciseEntries(newEntries);
                    setActiveTemplateKey(key);
                    setShowTemplateModal(false);
                  }}>
                  <Text style={{ fontSize: 15, fontWeight: '700', color: theme.colors.text }}>📋 {key}</Text>
                  <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 4 }}>{tmpl.exercises.map((e: any) => e.exerciseName).join(', ')}</Text>
                </TouchableOpacity>
              ))}

              {/* ── DUEL Built-in Templates (Pro) ── */}
              <View style={{ marginTop: 8, marginBottom: 4 }}>
                <Text style={{ fontSize: 13, fontWeight: '800', color: theme.colors.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                  ⚡ DUEL Templates
                </Text>
              </View>
              {(() => {
                const isPro = hasAtLeast(tier, 'pro');
                const categories = getTemplatesByCategory();
                return Object.entries(categories).map(([category, templates]) => (
                  <View key={category} style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 11, fontWeight: '700', color: theme.colors.textTertiary, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>
                      {category}
                    </Text>
                    {templates.map((tmpl: BuiltInTemplate) => (
                      <TouchableOpacity
                        key={tmpl.id}
                        style={{ backgroundColor: theme.colors.background, borderRadius: 2, padding: 14, marginBottom: 6, borderWidth: 1, borderColor: isPro ? theme.colors.border : theme.colors.border + '80' }}
                        onPress={() => {
                          if (!isPro) { router.push('/paywall'); setShowTemplateModal(false); return; }
                          const newEntries: ExerciseEntry[] = tmpl.exercises.map((te) => {
                            const matched = exercises.find((e) => e.name.toLowerCase() === te.exerciseName.toLowerCase()) ?? null;
                            return {
                              id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
                              exercise: matched ?? ({ id: `builtin_${te.exerciseName}`, name: te.exerciseName, body_part: '', is_custom: false } as any),
                              weight: '',
                              reps: String(te.targetReps.split('-')[0] || ''),
                              sets: String(te.targetSets),
                              isPR: false,
                              videoUri: null,
                              notes: te.notes || '',
                            };
                          });
                          setExerciseEntries(newEntries);
                          setActiveTemplateKey(tmpl.id);
                          setShowTemplateModal(false);
                        }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={{ fontSize: 15, fontWeight: '700', color: isPro ? theme.colors.text : theme.colors.textSecondary }}>
                            {tmpl.icon} {tmpl.name}
                          </Text>
                          {!isPro && <Text style={{ fontSize: 11, color: theme.colors.textTertiary }}>🔒</Text>}
                        </View>
                        <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>{tmpl.description}</Text>
                        <Text style={{ fontSize: 11, color: theme.colors.textTertiary, marginTop: 2 }}>
                          {tmpl.exercises.map((e) => e.exerciseName).join(' · ')}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                ));
              })()}

              {/* Create new template hint */}
              <View style={{ paddingVertical: 12, alignItems: 'center' }}>
                <Text style={{ fontSize: 12, color: theme.colors.textTertiary, textAlign: 'center' }}>
                  Add exercises to a workout, then tap "Save as Template" to create custom templates.
                </Text>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </Animated.View>
    <ConfirmDialog
      visible={!!confirmRemoveId}
      title="Remove Exercise"
      message="Are you sure you want to remove this exercise? Your logged sets will be lost."
      confirmLabel="Remove"
      onConfirm={() => {
        if (confirmRemoveId) {
          removeFromSuperset(confirmRemoveId);
          removeExerciseEntry(confirmRemoveId);
        }
        setConfirmRemoveId(null);
      }}
      onCancel={() => setConfirmRemoveId(null)}
    />
    </SafeAreaView>
    </ProfileGate>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { paddingTop: 12, paddingBottom: 20, paddingHorizontal: SPACE.lg, borderBottomWidth: 1 },
  dateHeaderText: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold, letterSpacing: TRACKING.tight, textTransform: 'uppercase' },
  dateButton: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  scrollView: { flex: 1 },
  scrollContent: { paddingHorizontal: SPACE.lg, paddingVertical: SPACE.md, paddingBottom: 40 },
  addExerciseButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1, padding: 20, borderRadius: RADIUS.none, marginBottom: 16 },
  addExerciseText: { fontSize: FONT.md, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },
  submitButton: { ...BUTTON.primary, alignItems: 'center' },
  submitButtonDisabled: { opacity: 0.6 },
  submitButtonText: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold, textTransform: 'uppercase' },
  saveTemplateBtn: { alignItems: 'center', paddingVertical: 12, borderRadius: RADIUS.none, borderWidth: 1, marginBottom: 8 },
  newWorkoutBtn: { alignItems: 'center', paddingVertical: 12, borderRadius: RADIUS.none, borderWidth: 1, marginBottom: 12 },
  saveTemplateText: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },
  actionRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  loadBaselineBtn: { alignItems: 'center', paddingVertical: 10, borderRadius: RADIUS.none, borderWidth: 1, marginBottom: 12 },
  loadBaselineBtnText: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },
  baselinePrompt: { ...CARD.standard, marginBottom: 16, alignItems: 'center', gap: 8 },
  baselinePromptTitle: { fontSize: FONT.md, fontFamily: TYPO.heading.bold, textAlign: 'center', textTransform: 'uppercase' },
  baselinePromptDesc: { fontSize: FONT.sm, fontFamily: TYPO.body.regular, textAlign: 'center', lineHeight: 18 },
  baselineLoadBtn: { ...BUTTON.primary, marginTop: 4 },
  baselineLoadBtnText: { fontSize: FONT.base, fontFamily: TYPO.heading.bold, textTransform: 'uppercase' },
  baselineDismiss: { fontSize: FONT.xs, fontFamily: TYPO.body.semibold, marginTop: 4 },
  workoutNameInput: { borderWidth: 1, borderRadius: RADIUS.xs, paddingHorizontal: 14, paddingVertical: 12, fontSize: FONT.md, fontFamily: TYPO.heading.semibold, marginBottom: 16 },
  sessionNotesInput: { borderWidth: 1, borderRadius: RADIUS.xs, paddingHorizontal: 14, paddingVertical: 12, fontSize: FONT.base, fontFamily: TYPO.body.regular, minHeight: 60, maxHeight: 120, textAlignVertical: 'top', marginBottom: 12 },
  addSupersetButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderWidth: 1, paddingVertical: 16, paddingHorizontal: 16, borderRadius: RADIUS.none, marginBottom: 16 },
  addSupersetText: { fontSize: FONT.sm, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },
  supersetWrapper: { flexDirection: 'row', marginBottom: 0 },
  supersetBar: { width: 4, borderRadius: RADIUS.xs, marginRight: 0 },
  supersetContent: { flex: 1, paddingLeft: 10 },
  supersetLabel: { ...SECTION_LABEL, fontFamily: TYPO.subheading.extrabold, marginBottom: 4 },
  startWorkoutBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, paddingVertical: 24, borderRadius: RADIUS.none, marginBottom: 16 },
  startWorkoutText: { fontSize: FONT.xl, fontFamily: TYPO.heading.bold, textTransform: 'uppercase' },
  newcomerTip: { ...CARD.standard, borderWidth: 1, marginBottom: 16, alignItems: 'center' as const, gap: 6 },
  newcomerTipTitle: { fontSize: FONT.sm, fontFamily: TYPO.heading.bold, textTransform: 'uppercase' as const, letterSpacing: TRACKING.wide },
  newcomerTipDesc: { fontSize: FONT.sm, fontFamily: TYPO.body.regular, textAlign: 'center' as const, lineHeight: 20 },
});


